import React, { useEffect, useMemo, useRef } from 'react';
import { useWatermarkStore } from 'store/watermarkStore';
import {
    WatermarkInfo,
    buildWatermarkBackgroundImage,
    buildWatermarkBackgroundSize,
    shouldRenderWatermark,
} from 'utils/watermark';

/**
 * 全站页面水印覆盖层。
 *
 * 挂在应用根（App 内）而不是 MainLayout 内，原因有三，缺一不可：
 *  1. antd 的 Modal / Drawer / Select 浮层都 portal 到 document.body，不在 MainLayout 子树里；
 *     而弹窗里恰恰是最敏感的明细数据（水印规范明确要求模态层内部也要铺）。
 *  2. `/wms/zipcode-map-geofence/map` 这类全屏页渲染在模块 Layout 之外，MainLayout 根本不参与。
 *  3. 挂根之后「全站」不需要任何路由判断 —— 登录页/教学视频页没有登录态，
 *     store 里就是 null，自然不渲染。
 *
 * 用 `position: fixed` + 单个 div + SVG data URI 平铺：全站恒定 1 个 DOM 节点，
 * 不参与 layout，`pointer-events: none` 保证不吃任何点击/拖拽/横向滚动。
 * 规范禁止逐个水印生成 DOM 节点（长表格滚动会被拖慢）。
 *
 * 打印：TMS BOL 的打印样式里有 `body > *:not(#bol-print-container) { display:none }`，
 * 本层在 #root 内会被一并隐藏，打印件不带水印 —— 符合「对外单据不带内部水印」的预期，
 * 无需额外适配。
 */

/** 高于 antd 全部浮层（Modal/Drawer 1000、message/notification 1010）。
 *  规范写的是 z-index 60，但那样会被弹窗盖住，与「模态层内部同样需要铺设水印」自相矛盾。 */
const WATERMARK_Z_INDEX = '2000';

/** 被篡改后重建的上报节流窗口，避免有人写循环脚本刷爆日志 */
const TAMPER_REPORT_INTERVAL_MS = 10 * 1000;

/** 必须守住的关键样式：任何一条被改掉，水印都会失效或失去覆盖能力 */
function criticalStyles(backgroundImage: string): Record<string, string> {
    return {
        position: 'fixed',
        top: '0',
        right: '0',
        bottom: '0',
        left: '0',
        'pointer-events': 'none',
        'z-index': WATERMARK_Z_INDEX,
        display: 'block',
        visibility: 'visible',
        opacity: '1',
        transform: 'none',
        'background-repeat': 'repeat',
        'background-position': '0 0',
        'background-size': buildWatermarkBackgroundSize(),
        'background-image': backgroundImage,
    };
}

function applyStyles(el: HTMLElement, backgroundImage: string): void {
    const styles = criticalStyles(backgroundImage);
    Object.entries(styles).forEach(([key, value]) => {
        // setProperty 带 important：业务侧的全局 CSS 或手改 style 都盖不掉
        el.style.setProperty(key, value, 'important');
    });
}

const Watermark: React.FC = () => {
    const info = useWatermarkStore((s) => s.info);
    const ref = useRef<HTMLDivElement>(null);
    const lastReportAtRef = useRef(0);

    const visible = shouldRenderWatermark(info);
    const backgroundImage = useMemo(
        () => (visible ? buildWatermarkBackgroundImage(info as WatermarkInfo) : ''),
        [visible, info]
    );

    useEffect(() => {
        const el = ref.current;
        if (!el || !backgroundImage) return;

        const parent = el.parentNode;
        applyStyles(el, backgroundImage);

        const report = (reason: string) => {
            const now = Date.now();
            if (now - lastReportAtRef.current < TAMPER_REPORT_INTERVAL_MS) return;
            lastReportAtRef.current = now;
            // 走 console.error 而不是自己发埋点：index.html 里的阿里云 RUM 开了 consoleError 采集，
            // 安全团队能直接在 RUM 里按这条前缀捞到异常行为，不需要新增上报通道。
            console.error('[watermark] tampered and restored', {
                reason,
                userId: info?.userId,
                path: window.location.pathname,
                at: new Date().toISOString(),
            });
        };

        const observer = new MutationObserver((records) => {
            let tampered = '';
            for (const record of records) {
                if (record.type === 'attributes') {
                    tampered = 'attributes';
                } else if (Array.from(record.removedNodes).includes(el)) {
                    tampered = 'removed';
                }
            }
            if (!tampered) return;

            if (parent && !parent.contains(el)) parent.appendChild(el);
            applyStyles(el, backgroundImage);
            // 丢弃本次恢复动作自己触发的记录，否则会自激成死循环
            observer.takeRecords();
            report(tampered);
        });

        // 只观察覆盖层自身的属性 + 父节点的直接子节点增删。
        // **不要**用 observe(document.body, { subtree: true })：那会在每次表格重渲染时
        // 触发大量回调，长列表滚动会明显变卡。
        observer.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });
        if (parent) observer.observe(parent, { childList: true });

        return () => observer.disconnect();
    }, [backgroundImage, info]);

    if (!visible) return null;

    return <div ref={ref} aria-hidden="true" data-testid="app-watermark" />;
};

export default Watermark;
